import React, { useState, useMemo } from 'react';
import { Pagination, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import './table.css';
import { useAccount } from 'wagmi';
import { DownOutlined, SearchOutlined } from '@ant-design/icons';

export interface RankingRow {
  address: string;
  points: number;
  rank: number;
}

interface TableColumn<T> {
  title: string;
  dataIndex: keyof T | string;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
}

interface SoulRankingTableProps {
  data: RankingRow[];
  className?: string;
  pageSize?: number;
  accountData?: RankingRow;
}

const PAGE_SIZE = 20; // 每页条数


const AVATAR_LIST = [
  '/soul/soul-rank-1.svg',
  '/soul/soul-rank-2.svg',
  '/soul/soul-rank-3.svg',
  '/soul/soul-rank-4.svg',
  '/soul/soul-rank-5.svg',
];

const SoulRankingTable: React.FC<SoulRankingTableProps> = ({
  data,
  className = '',
  accountData,
  pageSize = PAGE_SIZE,
}) => {
  const { address } = useAccount();
  // const address = '0xa31A940ecFB4Cb9fE7884eB3C9a959Db79CbdC70';
  const [current, setCurrent] = useState(1);
  const [addressFilter, setAddressFilter] = useState('');
  const [memberClassFilter, setMemberClassFilter] = useState<string>('all');
  const { t } = useTranslation('soul');
  
  // 会员等级选项
  const memberClassOptions = [
    { value: 'all', label: t('soul_all_members') },
    { value: 'Basic', label: t('soul_basic_member') },
    { value: 'Gold', label: t('soul_gold_member') },
    { value: 'Platinum', label: t('soul_platinum_member') },
    { value: 'Non-Member', label: t('soul_non_member') },
  ];

  // 获取会员等级
  const getMemberClass = (points: number) => {
    if (points >= 480) return 'Platinum';
    if (points >= 100) return 'Gold';
    if (points >= 48) return 'Basic';
    return 'Non-Member';
  };

  // 过滤数据
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      const addressMatch = !addressFilter || 
        row.address.toLowerCase().includes(addressFilter.toLowerCase());
      
      const memberClassMatch = memberClassFilter === 'all' || 
        getMemberClass(row.points) === memberClassFilter;
      let userAddressMatch = true;
      if (address) {
        userAddressMatch = row.address.toLowerCase() !== address.toLowerCase();
      }
      return addressMatch && memberClassMatch && userAddressMatch;
    });
  }, [data, addressFilter, memberClassFilter, address]);


  // 分页数据
  let pagedData = filteredData.slice((current - 1) * pageSize, current * pageSize);
  if (address) {
    pagedData = [accountData as RankingRow, ...pagedData];
  }
  const columns: TableColumn<RankingRow>[] = [
    {
      title: t('soul_rank'),
      dataIndex: 'rank',
      width: '20%',
      render: (value, row, rowIndex) => {
        const text = value.toString().padStart(2, '0');
        if (row.address?.toLowerCase() === address?.toLowerCase()) {
          return (<div className='pl-[24px]'>
            <div className='text-gray-500 text-16 font-normal font-mono'>{t('soul_my_rank')}</div>
            <div className='text-white text-24 font-bold font-mono'>{value === 0 ? t('soul_not_in_ranking') : text}</div>
          </div>)
        }
        return (
          <span className="pl-[24px] text-24 font-bold text-[#fff] font-mono">{text}</span>
        )
      }
    },
    {
      title: t('soul_wallet_address'),
      dataIndex: 'address',
      width: '45%',
      render: (value, row) => (
        <div className="flex items-center py-[28px] px-2">
          <img src={AVATAR_LIST[Math.floor(Math.random() * AVATAR_LIST.length)]} alt="avatar" className="w-[20px] sm:w-[32px] mr-[8px] rounded-full" />
          <span className="text-white text-sm font-mono break-all">{row.address}</span>
        </div>
      ),
    },
    {
      title: t('soul_points'),
      dataIndex: 'points',
      width: '15%',
      render: (value, row) => {
        let memberClass = 'Basic';
        if (Number(row.points) >= 480) {
          memberClass = 'Platinum';
        } else if (Number(row.points) >= 100) {
          memberClass = 'Gold';
        }
        const classColorMap = {
          Platinum: 'text-[#2ED3B7]',
          Gold: 'text-[#F670C7]',
          Basic: 'text-[#FFD166]',
        };
        return (<div>
          <div className="text-white text-base font-bold font-mono">{value}</div>
          <div className={`font-bold sm:hidden ${classColorMap[memberClass as keyof typeof classColorMap]}`}>{memberClass}</div>
          </div>)
      },
    },
    {
      title: t('soul_member_class'),
      dataIndex: 'memberClass',
      width: '10%',
      className: 'hidden sm:flex',
      render: (value, row) => {
        let memberClass = 'Non-Member';
        if (Number(row.points) >= 480) {
          memberClass = 'Platinum';
        } else if (Number(row.points) >= 100) {
          memberClass = 'Gold';
        } else if (Number(row.points) >= 48) {
          memberClass = 'Basic';
        }
        const classColorMap = {
          Platinum: 'text-[#2ED3B7]',
          Gold: 'text-[#F670C7]',
          Basic: 'text-[#FFD166]',
          'Non-Member': 'text-[#79716B]',
        };
        return (<div><div className={`font-bold ${classColorMap[memberClass as keyof typeof classColorMap]}`}>{memberClass}</div></div>);
      },
    },
  ];
  return (
    <div className={`w-full border border-[#44403C] rounded-[16px] p-4 overflow-x-auto ${className}`}>
      {/* Filter Section */}
      <div className="mb-6 flex flex-row sm:flex-row gap-4 items-start">
        <div className="w-[150px] sm:w-[200px]">
          <Input
            placeholder={t('soul_search_address_placeholder')}
            value={addressFilter}
            onChange={(e) => {
              setAddressFilter(e.target.value);
              setCurrent(1); // 重置到第一页
            }}
            className="search-input bg-gray-700 border-gray-700 text-gray-500 hover:text-gray-500 hover:bg-gray-700 hover:border-gray-500"
            prefix={<SearchOutlined className="text-gray-500" />}
            allowClear
          />
        </div>
        <div className="sm:w-48">
          <Select
            value={memberClassFilter}
            onChange={(value) => {
              setMemberClassFilter(value);
              setCurrent(1); // 重置到第一页
            }}
            options={memberClassOptions}
            className="h-[40px] table-select bg-gray-700 border-gray-700 text-gray-500 hover:text-gray-500 hover:border-gray-500"
            placeholder={t('soul_select_member_class')}
            suffixIcon={<DownOutlined className="text-gray-500" />}
          />
        </div>
      </div>
      
      {/* 桌面端 Table */}
      <div className="min-w-full sm:table">
        <div className="text-left flex items-center justify-between text-[#b0b0b0] text-xs border-b border-[#44403C]">
          {columns.map((column) => (
            <div
              key={column.dataIndex}
              className={`py-3 px-2 font-normal ${column.className || ''}`}
              style={{ width: column.width }}
            >
              {column.title}
            </div>
          ))}
        </div>
        {pagedData.length > 0 ? pagedData.map((row, rowIndex) => {
          
          return (
          <div
            key={rowIndex}
            className={`flex items-center justify-between border-b border-[#232323] last:border-0 group mt-[8px] min-h-[104px]`}
          >
            {columns.map((column, index) => (
              <div
                key={column.dataIndex}
                className={`relative flex items-center overflow-hidden align-middle h-[104px] ${column.className || ''}`}
                style={{ width: column.width }}
              >
                {column.render ? column.render(row[column.dataIndex as keyof RankingRow], row, rowIndex) : row[column.dataIndex as keyof RankingRow]}
              </div>
            ))}
          </div>
          );
        }) : (
          <div className="text-center text-white text-base font-bold font-mono pt-[24px]">
            {t('soul_no_data')}
          </div>
        )
      }
      </div>
      {/* 分页器 */}
      {
        filteredData.length > 0 && (
          <div className="mt-4 flex justify-center">
        {/* 桌面端分页 */}
        <div className="hidden sm:block">
          <Pagination
            current={current}
            pageSize={pageSize}
            total={filteredData.length}
            onChange={setCurrent}
            showSizeChanger={false}
            className='soul-pagination'
          />
        </div>
        {/* 移动端分页 */}
        <div className="sm:hidden flex items-center justify-between gap-4 w-full">
          <button
            className="bg-[#232323] rounded px-3 py-1 text-lg text-[#b0b0b0] disabled:opacity-50"
            disabled={current === 1}
            onClick={() => setCurrent((c) => Math.max(1, c - 1))}
          >
            &larr;
          </button>
          <span className="text-[#b0b0b0] text-base">
            {t('soul_page')} {current} {t('soul_of')} {Math.ceil(filteredData.length / pageSize)}
          </span>
          <button
            className="bg-[#232323] rounded px-3 py-1 text-lg text-[#b0b0b0] disabled:opacity-50"
            disabled={current === Math.ceil(filteredData.length / pageSize)}
            onClick={() => setCurrent((c) => Math.min(Math.ceil(filteredData.length / pageSize), c + 1))}
          >
            &rarr;
          </button>
        </div>
      </div>
        )
      }
    </div>
  );
};

export default SoulRankingTable;